import { useRouter } from "@tanstack/react-router";
import { formatEncounterAppointmentStatusTag } from "@workspace/lib/date";
import { Badge } from "@workspace/web-ui/components/badge";
import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/web-ui/components/card";
import { Sheet, SheetContent } from "@workspace/web-ui/components/sheet";
import { cn } from "@workspace/web-ui/lib/utils";
import { AlertCircle, Calendar, CalendarOff, Check, CheckCircle, ChevronLeftIcon, Circle, Clock, Info, Video } from "lucide-react";
import { useEffect, useState } from "react";
import { Appointment } from "~/features/appointments/types";
import { MedicationPaymentModal } from "~/features/dashboard/components/medication-payment";
import { useMedicationSelectionStore } from "~/features/dashboard/store/medication-ids";
import { formatAppointmentDateTime, formatFullDate, formatTime } from "~/lib/date-utils";

interface AppointmentUIProps {
    appointments: Appointment[];
}

const getStatusConfig = (appointment: Appointment) => {
    let { status, tag } = formatEncounterAppointmentStatusTag({
        date: appointment.date,
        time: appointment.time,
        status: appointment.status as "scheduled" | "attended" | "cancelled",
        meetingInterval: appointment.time_interval_minutes
    })

    if (appointment.status === "attended" && appointment.encounter?.recommended_tasks?.some(task => task.status === "pending")) {
        status = "actionNeeded"
        tag = "Action Needed"
    }

    const statusMap = {
        "happening": { color: "bg-[#EBF0FB] text-[#4272DD]", icon: <Video size={14} />, text: tag, status: "happening" },
        "upcoming": { color: "bg-[#FFF6E7] text-[#FFAB1F]", icon: <Calendar size={14} />, text: tag, status: "upcoming" },
        "past": { color: "bg-[#F1F1F1] text-[#7F8493]", icon: <CalendarOff size={14} />, text: tag, status: "past" },
        "cancelled": { color: "bg-[#F1F1F1] text-[#7F8493]", icon: <CalendarOff size={14} />, text: tag, status: "cancelled" },
        "completed": { color: "bg-[#EBFBF1] text-[#14AD4D]", icon: <CheckCircle size={14} />, text: tag, status: "completed" },
        "actionNeeded": { color: "bg-[#EBF0FB] text-[#4272DD]", icon: <AlertCircle size={14} />, text: tag, status: "actionNeeded" },
    };

    return statusMap[status as keyof typeof statusMap];
};

export default function AppointmentUi({ appointments }: AppointmentUIProps) {
    const [selectedAppointment, setSelectedAppointment] = useState(appointments[0]!);
    const [isMobile, setIsMobile] = useState(false)
    const [openSheet, setOpenSheet] = useState(false)
    const [prescriptionId, setPrescriptionId] = useState("")
    //request for medication to pay for medication
    // const [requestMed, setRequestMed] = useState(false);

    //Setting ids to store and making payment modal open
    const { isReady, closeIsReady, setIsReady } = useMedicationSelectionStore()

   
    const router = useRouter()

    const handleSelectAppointment = (id: string) => {
        router.navigate({ to: `/dashboard/weight-loss/appointments/reschedule-booking?appointment_id=${id}`, }); // Navigate after setting the value to reschedule booking
    };

    const handleRecommendedTaskAppointment = () => {
        router.navigate({ to: '/dashboard/weight-loss/appointments/booking', }); // Navigate to appointment booking page
    }

    const appointmentStatus = getStatusConfig(selectedAppointment)?.status

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768) // Tailwind's md breakpoint
        }

        handleResize()
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    return (
        <div className="flex sm:gap-6 w-full">
            {/* Sidebar */}
            <div className="sm:w-2/5 lg:w-2/4 w-full ">

                <div className="sm:max-h-screen sm:overflow-auto md:overflow-y-auto hide-scrollbar w-full" >
                    <div className="space-y-4 sm:pr-7 md:pr-2 w-full" >
                        {appointments
                            .slice() // Create a copy to avoid mutating the original array
                            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) // Sort by earliest date
                            .map((appointment, index) => {
                                const statusConfig = getStatusConfig(appointment)

                                return (
                                    <Card key={index} onClick={() => {
                                        setSelectedAppointment(appointment)
                                        if (isMobile) {
                                            setOpenSheet(true)
                                        }
                                    }} className={cn(`cursor-pointer max-w-full`,
                                        selectedAppointment === appointment ? "border border-[#7F7D83] shadow-xl" : "",
                                    )}>
                                        <CardContent className="p-4 space-y-3 ">
                                            <h3 className="font-semibold text-xl">{appointment.title}</h3>
                                            <p className="text-sm text-[#2D2F35] font-medium flex items-center gap-2">
                                                <Calendar size={14} className="text-[#7F8493]" /> {formatAppointmentDateTime(appointment.date, appointment.time)}
                                            </p>

                                            <div className={`inline-flex rounded-3xl items-center gap-2  px-2 py-1 ${statusConfig.color}`}>
                                                {statusConfig.icon}
                                                <span className="text-sm font-medium">{statusConfig.text}</span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )
                            })}
                    </div>
                </div>
            </div>

            {/* Main Content: mobile view */}
            <div className="sm:hidden min-h-screen">
                {isMobile && (
                    <Sheet open={openSheet} onOpenChange={setOpenSheet} >
                        <SheetContent title="appointment-details" side="right" className="w-full max-w-none overflow-auto  h-full [&>button:last-child]:hidden">
                            <Button onClick={() => setOpenSheet(false)} className="bg-white p-5 mb-5 border-[#DCDCDE] rounded-lg text-[#202020CC] ">
                                <ChevronLeftIcon />
                                Go back
                            </Button>
                            <div className=" space-y-4 ">
                                <h2 className="text-3xl font-medium text-[#2D2F35]">{selectedAppointment?.title}</h2>
                                <div className="space-y-2">
                                    <span className="text-sm text-[#2D2F35] flex items-start gap-4">
                                        <Calendar size={16} className="my-2 text-[#7F8493]" />
                                        <span className="flex flex-col space-y-1"><h6 className="">Date</h6>  <h6 className="text-xs text-[#202020CC]">{formatFullDate(selectedAppointment?.date)} </h6>  </span>
                                    </span>
                                    <span className="text-sm text-[#2D2F35] flex items-start gap-4">
                                        <Clock size={16} className="my-2 text-[#7F8493]" />
                                        <span className="flex flex-col space-y-1"><h6 className="">Time</h6>  <h6 className="text-xs text-[#202020CC]">{formatTime(selectedAppointment?.time)} </h6>  </span>
                                    </span>
                                    <span className="text-sm text-[#2D2F35] flex items-start gap-4">
                                        <Video size={16} className="my-2 text-[#7F8493]" />
                                        <span className="flex flex-col space-y-1"><h6 className="">Location</h6>  <h6 className="text-xs text-[#202020CC]">{selectedAppointment?.location}</h6>  </span>
                                    </span>
                                    <div className="bg-[#EBF0FB] px-4 py-3 gap-3 rounded-lg flex items-center justify-between flex-col sm:flex-col space-y-3">
                                        <div className="flex items-top sm:items-center gap-3">
                                            <div className="flex items-center justify-center size-8">
                                                <Info className="text-[#4272DD] size-5" />
                                            </div>

                                            <span className="text-sm text-[#202020CC]  flex items-center ">
                                                {appointmentStatus === "upcoming"
                                                    ? `Your appointment is scheduled for ${formatAppointmentDateTime(selectedAppointment?.date, selectedAppointment?.time)}. Please ensure you're available 5 minutes before the start time.`
                                                    : appointmentStatus === "happening" ? "This appointment is live" : appointmentStatus === "completed" ? "This appointment was successfully held." : appointmentStatus === "actionNeeded" ? "This appointment was successfully held." : "This appointment did not hold."}
                                            </span>
                                        </div>

                                        {appointmentStatus !== "actionNeeded" && appointmentStatus !== "completed" && appointmentStatus !== "cancelled" ?
                                            <a href={selectedAppointment?.zoom_metadata.join_url}
                                                target="_blank" rel="noopener noreferrer"
                                                className={cn(buttonVariants({ variant: "default" }), "bg-[#4272DD] rounded-lg w-full sm:w-auto", appointmentStatus !== "happening" ? "pointer-events-none cursor-not-allowed opacity-60 no-underline" : "")}>
                                                Join meeting
                                            </a> : null}

                                        {appointmentStatus !== "happening" && appointmentStatus !== "actionNeeded" && appointmentStatus !== "completed" ?
                                            <Button
                                                className="border-2 border-[#4272DD] bg-inherit hover:bg-[#c6d5f6] rounded-lg text-[#4272DD] text-sm"
                                                onClick={() => handleSelectAppointment(selectedAppointment?.appointment_id ?? "")} >
                                                Reschedule
                                            </Button> : null}
                                    </div>
                                </div>

                                {/* Appointment */}
                                {appointmentStatus !== "actionNeeded" && appointmentStatus !== "completed" ? (
                                    <Card className="w-full  bg-[#F1F1F1] shadow-md rounded-lg">
                                        {/* Card Header */}
                                        <CardHeader className="px-5 py-3">
                                            <CardTitle className="text-xl font-semibold">Appointment Information</CardTitle>
                                        </CardHeader>

                                        {/* Card Content */}
                                        <CardContent className="space-y-4 px-5 text-[#202020CC] text-sm sm:text-base">
                                            <p className="">
                                                This appointment will be conducted via video call. Please ensure you have:
                                            </p>

                                            {/* List of Preparations */}
                                            <ul className="space-y-1 list-disc mx-7">
                                                <li>A stable internet connection</li>
                                                <li>A quiet, private space for your appointment</li>
                                                <li>Any relevant medical documents or information ready</li>
                                                <li>A list of any questions or concerns you wish to discuss</li>
                                            </ul>
                                        </CardContent>
                                    </Card>
                                ) : null}

                                {/* Summary */}
                                {/* {(appointmentStatus === "completed" || appointmentStatus === "actionNeeded") && (
                                    <Card className="bg-[#F1F1F1]">
                                        <CardContent className="p-4 space-y-2">
                                            <span className="flex justify-between items-center"> <h3 className="font-semibold text-xl">Summary</h3> <h6 className="text-[#202020CC] font-normal text-sm">Date: {"23rd Mar, 2025"} </h6> </span>

                                            <p className="text-sm lg:w-3/4">{selectedAppointment?.summary}</p>
                                        </CardContent>
                                    </Card>
                                )} */}

                                {/* Recommended task */}
                                {(appointmentStatus === "completed" || appointmentStatus === "actionNeeded") && selectedAppointment?.encounter?.recommended_tasks && selectedAppointment.encounter.recommended_tasks.length > 0 && (
                                    // Check if there are recommended tasks
                                    <Card className="bg-[#F1F1F1]" >
                                        <CardContent className="p-4 flex flex-col justify-between">
                                            <h3 className="font-semibold pb-3">Recommended Tasks</h3>
                                            {selectedAppointment?.encounter?.recommended_tasks.map((task, index) =>

                                            (
                                                <div className="flex justify-between items-center my-2  bg-white p-3 rounded-xl" key={index}>

                                                    <div className="inline-flex gap-3 items-center">
                                                        {task?.status !== "pending" ? <div className="flex size-6 items-center justify-center rounded-full bg-[#2A6BFF]">
                                                            <Check
                                                                className="size-3 text-white"
                                                                strokeWidth={3}
                                                            />
                                                        </div> : <Circle
                                                            strokeWidth={2}
                                                            className="size-6 text-[#4272DD]"
                                                        />}


                                                        <p className="text-sm text-gray-500">{task?.type === "appointment_followup" ? "Schedule Appointment" : "Pay for Medication"}</p>
                                                        {task?.status && <Badge className="text-[#9D6508] bg-[#FFF1DA] py-1 px-2 hover:bg-[#FFF1DA]">Priority</Badge>}
                                                    </div>
                                                    <Button className="rounded-lg bg-[#2A6BFF]" disabled={task?.status !== "pending"} onClick={() => {
                                                        if (task?.type === "appointment_followup") {
                                                            handleRecommendedTaskAppointment()
                                                        }
                                                        if (task?.type === "medication_payment") {
                                                            setPrescriptionId(task?.prescription_id)
                                                            setIsReady(true)
                                                            // setRequestMed(isReady)
                                                            // setIds(task?.prescription_id, task?.medication_id)
                                                        }

                                                    }}>{task?.type === "appointment_followup" ? "Schedule Now" : "Pay Now"}</Button>
                                                </div>
                                            )
                                            )}
                                        </CardContent>

                                        {isReady && prescriptionId && <MedicationPaymentModal
                                            prescription_id={prescriptionId}
                                            open={isReady}
                                            onClose={() => closeIsReady()}
                                        />}
                                    </Card>
                                )}
                            </div>
                        </SheetContent>
                    </Sheet>
                )}
            </div>

            {/* Main Content */}
            <div className="w-2/3 hidden sm:flex sm:flex-col space-y-4 sticky right-0 top-48 h-screen overflow-y-auto">
                <div className="flex justify-between items-center">
                    <h2 className="text-3xl font-medium text-[#2D2F35]">{selectedAppointment?.title}</h2>
                    {appointmentStatus !== "actionNeeded" && appointmentStatus !== "completed" && appointmentStatus !== "cancelled" ?
                        <a href={selectedAppointment?.zoom_metadata.join_url}
                            target="_blank" rel="noopener noreferrer"
                            className={cn(buttonVariants({ variant: "default" }), "bg-[#4272DD] rounded-lg w-full sm:w-auto", appointmentStatus !== "happening" ? "pointer-events-none cursor-not-allowed opacity-60 no-underline" : "")}>
                            Join meeting
                        </a> : null}
                </div>

                <div className="space-y-2">
                    <span className="text-sm text-[#2D2F35] flex items-start gap-4">
                        <Calendar size={16} className="my-2 text-[#7F8493]" />
                        <span className="flex flex-col  space-y-1"><h6 className="">Date</h6>  <h6 className="text-xs text-[#202020CC]">{formatFullDate(selectedAppointment?.date)} </h6>  </span>
                    </span>
                    <span className="text-sm text-[#2D2F35] flex items-start gap-4">
                        <Clock size={16} className="my-2 text-[#7F8493]" />
                        <span className="flex flex-col space-y-1"><h6 className="">Time</h6>  <h6 className="text-xs text-[#202020CC]">{formatTime(selectedAppointment?.time)} </h6>  </span>
                    </span>
                    <span className="text-sm text-[#2D2F35] flex items-start gap-4">
                        <Video size={16} className="my-2 text-[#7F8493]" />
                        <span className="flex flex-col space-y-1"><h6 className="">Location</h6>  <h6 className="text-xs text-[#202020CC]">{selectedAppointment?.location}</h6>  </span>
                    </span>
                    <div className="bg-[#EBF0FB] px-4 py-3 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center size-8">
                                <Info className="text-blue-500 size-5" />
                            </div>
                            <span className="text-sm text-[#202020CC]  flex items-center ">
                                {appointmentStatus === "upcoming"
                                    ? `Your appointment is scheduled for ${formatAppointmentDateTime(selectedAppointment?.date, selectedAppointment?.time)}. Please ensure you're available 5 minutes before the start time.`
                                    : appointmentStatus === "happening" ? "This appointment is live" : appointmentStatus === "completed" ? "This appointment was successfully held." : appointmentStatus === "actionNeeded" ? "This appointment was successfully held." : "This appointment did not hold."}
                            </span>
                        </div>
                        {appointmentStatus !== "happening" && appointmentStatus !== "actionNeeded" && appointmentStatus !== "completed" ?
                            <Button
                                className="border-2 border-[#4272DD] bg-inherit hover:bg-[#c6d5f6] rounded-lg text-[#4272DD] text-sm"
                                onClick={() => handleSelectAppointment(selectedAppointment?.appointment_id ?? "")} >
                                Reschedule
                            </Button> : null}
                    </div>
                </div>

                {/* Appointment */}
                {appointmentStatus !== "actionNeeded" && appointmentStatus !== "completed" ? (
                    <Card className="w-full  bg-[#F1F1F1] shadow-md rounded-lg">
                        {/* Card Header */}
                        <CardHeader className="px-5 py-3">
                            <CardTitle className="text-xl font-semibold">Appointment Information</CardTitle>
                        </CardHeader>

                        {/* Card Content */}
                        <CardContent className="space-y-4 px-5 text-[#202020CC] text-base">
                            <p className="">
                                This appointment will be conducted via video call. Please ensure you have:
                            </p>

                            {/* List of Preparations */}
                            <ul className="space-y-1 list-disc mx-7">
                                <li>A stable internet connection</li>
                                <li>A quiet, private space for your appointment</li>
                                <li>Any relevant medical documents or information ready</li>
                                <li>A list of any questions or concerns you wish to discuss</li>
                            </ul>
                        </CardContent>
                    </Card>
                ) : null}

                {/* Summary */}
                {/* {(appointmentStatus === "completed" || appointmentStatus === "actionNeeded") && (
                    <Card className="bg-[#F1F1F1]">
                        <CardContent className="p-4 space-y-2">
                            <span className="flex justify-between items-center"> <h3 className="font-semibold text-xl">Summary</h3> <h6 className="text-[#202020CC] font-normal text-sm">Date: {"23rd Mar, 2025"} </h6> </span>

                            <p className="text-sm w-3/4">{selectedAppointment?.summary}</p>
                        </CardContent>
                    </Card>
                )} */}

                {/* Recommended task */}
                {(appointmentStatus === "completed" || appointmentStatus === "actionNeeded") && selectedAppointment?.encounter?.recommended_tasks && selectedAppointment.encounter.recommended_tasks.length > 0 && (
                    // Check if there are recommended tasks
                    <Card className="bg-[#F1F1F1]" >
                        <CardContent className="p-4 flex flex-col justify-between">
                            <h3 className="font-semibold pb-3">Recommended Tasks</h3>
                            {selectedAppointment?.encounter?.recommended_tasks.map((task, index) =>

                            (
                                <div className="flex justify-between items-center my-2  bg-white p-3 rounded-xl" key={index}>
                                    <div className="inline-flex gap-3 items-center">
                                        {task?.status !== "pending" ? <div className="flex size-6 items-center justify-center rounded-full bg-[#2A6BFF]">
                                            <Check
                                                className="size-3 text-white"
                                                strokeWidth={3}
                                            />
                                        </div> : <Circle
                                            strokeWidth={2}
                                            className="size-6 text-[#4272DD]"
                                        />}

                                        <p className="text-sm text-gray-500">{task?.type === "appointment_followup" ? "Schedule Appointment" : "Pay for Medication"}</p>
                                        {task?.status && <Badge className="text-[#9D6508] bg-[#FFF1DA] py-1 px-2 hover:bg-[#FFF1DA]">Priority</Badge>}

                                    </div>
                                    <Button className="rounded-lg bg-[#2A6BFF]" disabled={task?.status !== "pending"} onClick={() => {
                                        console.log("medication_payment", task)
                                        console.log("clicked")

                                        if (task?.type === "appointment_followup") {
                                            handleRecommendedTaskAppointment()
                                        }
                                        if (task?.type === "medication_payment") {

                                            setPrescriptionId(task?.prescription_id)
                                            setIsReady(true)
                                            // setRequestMed(isReady)
                                            // setIds(task?.prescription_id, task?.medication_id)
                                        }

                                    }}>{task?.type === "appointment_followup" ? "Schedule Now" : "Pay Now"}</Button>
                                </div>
                            )
                            )}
                        </CardContent>

                        {isReady && prescriptionId && <MedicationPaymentModal
                            prescription_id={prescriptionId}
                            open={isReady}
                            onClose={() => closeIsReady()}
                        />}
                    </Card>
                )}
            </div>
        </div>
    );
}
